import { FC, useState } from 'react';
import {
  Stack,
  TextInput,
  Button,
  Group,
  Text,
  Card,
  Divider,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconSearch,
  IconUserPlus,
  IconAlertCircle,
  IconX,
} from '@tabler/icons-react';
import { Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { BirthDateInput } from '@/components/inputs/BirthDateInput';
import { usePatientSearch } from '@hooks/usePatientSearch';
import { PatientCard } from './PatientCard';

interface PatientSearchProps {
  onPatientSelected: (patient: Patient, isNew: false) => void;
  onCreateNewPatient: () => void;
}

/**
 * Patient search component for patient onboarding workflow
 * Supports search by AMKA or demographics (last name + DOB)
 *
 * @param props - Component props
 * @param props.onPatientSelected - Callback when patient is selected
 * @param props.onCreateNewPatient - Callback when "Create New Patient" is clicked
 * @returns JSX.Element
 */
export const PatientSearch: FC<PatientSearchProps> = ({
  onPatientSelected,
  onCreateNewPatient,
}) => {
  const { t } = useTranslation('patient-onboarding');

  // Use custom patient search hook
  const {
    searchByAmka,
    searchByDemographics,
    results: searchResults,
    searching,
    hasSearched,
    amkaError: hookAmkaError,
    demographicError: hookDemographicError,
    lastSearchCriteria,
    validateAmka,
  } = usePatientSearch();

  // Form state
  const [amkaSearch, setAmkaSearch] = useState('');
  const [lastNameSearch, setLastNameSearch] = useState('');
  const [dobSearch, setDobSearch] = useState<string | null>(null);

  // Translate errors from hook
  const amkaError = hookAmkaError
    ? t(
        hookAmkaError.includes('11 digits')
          ? 'search.errors.invalidAmka'
          : 'search.errors.searchFailed'
      )
    : null;

  const demographicError = hookDemographicError
    ? t(
        hookDemographicError.includes('3 characters')
          ? 'search.errors.lastNameTooShort'
          : 'search.errors.searchFailed'
      )
    : null;

  // Search handlers
  const handleAmkaSearch = async () => {
    await searchByAmka(amkaSearch);
  };

  const handleDemographicSearch = async () => {
    await searchByDemographics(lastNameSearch, dobSearch);
  };

  const handleClearDemographicSearch = () => {
    setLastNameSearch('');
    setDobSearch(null);
  };



  return (
    <Stack gap="lg" pos="relative">
      <LoadingOverlay visible={searching} />

      <div>
        <Text size="xl" fw={700} mb="md">
          {t('search.title')}
        </Text>

        {/* AMKA Search */}
        <Card withBorder p="md" mb="md">
          <Text size="sm" fw={600} mb="xs">
            {t('search.searchByAmka')}
          </Text>
          <Group>
            <TextInput
              placeholder={t('search.amkaPlaceholder')}
              value={amkaSearch}
              onChange={(e) => setAmkaSearch(e.currentTarget.value)}
              maxLength={11}
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAmkaSearch();
                }
              }}
              error={amkaError}
            />
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleAmkaSearch}
              disabled={!validateAmka(amkaSearch)}
            >
              {t('search.searchButton')}
            </Button>
          </Group>
        </Card>

        <Divider
          label={t('search.orDivider')}
          labelPosition="center"
          my="md"
        />

        {/* Demographics Search */}
        <Card withBorder p="md">
          <Text size="sm" fw={600} mb="xs">
            {t('search.searchByDemographics')}
          </Text>
          <Stack gap="sm">
            <TextInput
              label={t('search.lastNamePlaceholder')}
              placeholder={t('search.lastNamePlaceholder')}
              value={lastNameSearch}
              onChange={(e) => setLastNameSearch(e.currentTarget.value)}
              minLength={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleDemographicSearch();
                }
              }}
            />
            <BirthDateInput
              label={t('search.dobLabel')}
              value={dobSearch}
              onChange={setDobSearch}
              size="sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleDemographicSearch();
                }
              }}
            />
            <Group gap="sm">
              <Button
                leftSection={<IconSearch size={16} />}
                onClick={handleDemographicSearch}
                disabled={lastNameSearch.length < 3}
                style={{ flex: 1 }}
              >
                {t('search.searchButton')}
              </Button>
              <Button
                leftSection={<IconX size={16} />}
                onClick={handleClearDemographicSearch}
                variant="light"
                color="gray"
              >
                {t('search.clearButton')}
              </Button>
            </Group>
            {demographicError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
              >
                {demographicError}
              </Alert>
            )}
          </Stack>
        </Card>
      </div>

      {/* Search Results */}
      {hasSearched && (
        <div>
          {searchResults.length === 0 ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={t('search.noResults')}
              color="blue"
            >
              <Text size="sm" mb="xs">
                {lastSearchCriteria?.type === 'amka'
                  ? t('search.noResultsWithAmka', { amka: lastSearchCriteria.amka })
                  : lastSearchCriteria?.type === 'demographics'
                    ? t('search.noResultsWithDemographics', {
                        lastName: lastSearchCriteria.lastName,
                        dob: lastSearchCriteria.dob,
                      })
                    : t('search.noResultsDesc')}
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                {t('search.noResultsDesc')}
              </Text>
              <Button
                leftSection={<IconUserPlus size={16} />}
                onClick={onCreateNewPatient}
                variant="light"
              >
                {t('search.createNewButton')}
              </Button>
            </Alert>
          ) : (
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  {t('search.resultsFound', { count: searchResults.length })}
                </Text>
                <Button
                  leftSection={<IconUserPlus size={16} />}
                  onClick={onCreateNewPatient}
                  variant="subtle"
                  size="xs"
                >
                  {t('search.createNewButton')}
                </Button>
              </Group>

              {searchResults.map((patient) => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  onSelect={() => onPatientSelected(patient, false)}
                  t={t}
                />
              ))}
            </Stack>
          )}
        </div>
      )}

      {/* Create New Button (always visible) */}
      {!hasSearched && (
        <Button
          leftSection={<IconUserPlus size={16} />}
          onClick={onCreateNewPatient}
          variant="light"
          fullWidth
          mt="xl"
        >
          {t('search.createNewButton')}
        </Button>
      )}
    </Stack>
  );
};
